import React from "react";
import "./App.css";
import qbBadge from "url:../public/quickbooks-badge.png";

const services = [
  { title: "Bookkeeping", desc: "Day-to-day transaction recording and journal entries - kept accurate and up to date." },
  { title: "Accounts Receivable", desc: "Client invoicing, payment tracking, and follow-up on overdue balances." },
  { title: "Accounts Payable", desc: "Bill entry, payment scheduling, and vendor account management." },
  { title: "Bank & Credit Card Reconciliation", desc: "Monthly reconciliation of all accounts to keep your books clean and audit-ready." },
  { title: "GST / HST Filing", desc: "Proper tracking and timely filing of all sales tax obligations with the CRA." },
  { title: "Payroll Processing & Remittance", desc: "On-time payroll runs and CRA remittances so you never miss a deadline." },
  { title: "T4 / T5 Preparation", desc: "Year-end slips prepared and filed accurately for all employees and shareholders." },
  { title: "Financial Statements & Cash Flow", desc: "Income statements, balance sheets, and cash flow reports that give you a clear picture." },
  { title: "Year-End Preparation", desc: "Organized financials handed off to your accountant - ready for tax season." },
  { title: "QuickBooks Online Setup", desc: "Chart of accounts, integrations, and workflows configured correctly from day one." },
  { title: "Client Onboarding & Catch-Up", desc: "Starting fresh or behind on your books? We get everything reconciled and current before your first month begins." },
];

const tiers = [
  { name: "Solo",    price: 200, volume: "< 10 transactions / mo",  accounts: "1 account" },
  { name: "Starter", price: 300, volume: "10-50 transactions / mo", accounts: "1-2 accounts" },
  { name: "Growing", price: 450, volume: "50-150 transactions / mo", accounts: "1-2 accounts" },
];

const includedServices = [
  "Bookkeeping",
  "Accounts Receivable",
  "Accounts Payable",
  "Bank & Credit Card Reconciliation",
  "GST / HST Filing",
  "Financial Statements & Cash Flow",
  "Year-End Preparation",
  "QuickBooks Online Setup",
  "Client Onboarding & Catch-Up",
];

const App: React.FC = () => {
  return (
    <div className="page">
      <nav className="nav">
        <span className="nav-logo">E1 Accounting</span>
        <a className="nav-cta" href="mailto:info@e1accounting.ca">Get in Touch</a>
      </nav>

      <section className="hero">
        <div className="hero-inner">
          <p className="hero-eyebrow">Full-Service Bookkeeping for Small Businesses</p>
          <h1 className="hero-title">Your books, handled<br />with precision.</h1>
          <p className="hero-sub">
            We take the stress out of bookkeeping so you can focus on running your business.
            Accurate records, on-time filings, and a partner who speaks your language.
          </p>
          <a className="btn-primary" href="mailto:info@e1accounting.ca">
            info@e1accounting.ca
          </a>
        </div>
      </section>

      <section className="services">
        <div className="container">
          <h2 className="section-title">What We Do</h2>
          <p className="section-sub">Core services - and much more tailored to your needs.</p>
          <div className="services-grid">
            {services.map((s) => (
              <div className="service-card" key={s.title}>
                <div className="service-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <div>
                  <h3 className="service-title">{s.title}</h3>
                  <p className="service-desc">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="services-note">T2 corporate tax returns are not prepared in-house - but we work closely with trusted CPAs and are happy to make a referral.</p>
        </div>
      </section>

      <section className="pricing">
        <div className="container">
          <h2 className="section-title">Transparent Pricing</h2>
          <p className="section-sub">Every plan includes the full service and a QuickBooks Online subscription. Price scales with your transaction volume.</p>
          <div className="pricing-grid">
            {tiers.map((t) => (
              <div className="pricing-card" key={t.name}>
                <p className="pricing-tier">{t.name}</p>
                <p className="pricing-price"><span className="pricing-amount">${t.price}</span><span className="pricing-period"> / mo</span></p>
                <p className="pricing-volume">{t.volume}</p>
                <p className="pricing-accounts">{t.accounts}</p>
                <a className="pricing-cta" href="mailto:info@e1accounting.ca">Get Started</a>
              </div>
            ))}
            <div className="pricing-card pricing-card--talk">
              <p className="pricing-tier">Need more?</p>
              <p className="pricing-talk-headline">Let's talk</p>
              <p className="pricing-volume">150+ transactions / mo</p>
              <p className="pricing-accounts">Multiple accounts</p>
              <a className="pricing-cta" href="mailto:info@e1accounting.ca">Get in Touch</a>
            </div>
          </div>
          <div className="pricing-includes">
            <p className="pricing-includes-title">All plans include</p>
            <ul className="pricing-includes-list">
              {includedServices.map((s) => (
                <li key={s}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {s}
                </li>
              ))}
            </ul>
          </div>
          <div className="pricing-addon">
            <p className="pricing-addon-title">Payroll Add-on - <strong>$35 / person / mo</strong></p>
            <p className="pricing-addon-desc">Includes payroll processing, T4 preparation, and source deduction remittance. Can be added to any plan.</p>
          </div>
        </div>
      </section>

      <section className="credentials">
        <div className="container credentials-inner">
          <h2 className="section-title">Credentials</h2>
          <div className="cred-cards">
            <div className="cred-card">
              <img src={qbBadge} alt="Certified QuickBooks Online ProAdvisor" className="cred-badge" />
              <div>
                <p className="cred-label">Certified</p>
                <p className="cred-name">QuickBooks Online ProAdvisor</p>
                <p className="cred-detail">Intuit-certified expertise in QuickBooks Online setup, migration, and ongoing management.</p>
              </div>
            </div>
            <div className="cred-card">
              <div>
                <p className="cred-label">Certified</p>
                <p className="cred-name">Algonquin College of Applied Arts and Technology</p>
                <p className="cred-detail">Professional bookkeeping certificate from Algonquin College of Applied Arts and Technology, Ottawa.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="contact">
        <div className="container contact-inner">
          <h2 className="contact-title">Ready to get started?</h2>
          <p className="contact-sub">Send us an email and we'll get back to you within one business day.</p>
          <a className="btn-primary" href="mailto:info@e1accounting.ca">info@e1accounting.ca</a>
        </div>
      </section>

      <footer className="footer">
        <div className="container footer-inner">
          <span>© {new Date().getFullYear()} E1 Accounting. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
};

export default App;
